// "Soluzioni compatibili": for every screw compatible with the current data (same filters as the menus) the minimum
// number of screws (rows × cols, centred grid) satisfying all the active checks (shear; combined load and withdrawal
// only with Fa > 0). Port of findCompatibleSolutions(): works on a copy of the rendered controls (the HTML mutated
// the real fields and restored them), yields to the event loop periodically and can be cancelled.

import type { EngineCatalog } from "./catalog";
import { compute } from "./compute";
import type { ControlValues } from "./controls";
import { cloneCtx, type EngineCtx } from "./ctx";
import {
  filterEnv,
  populateRondelle,
  predrillRequirement,
  rondelleCompatibiliList,
  screwPassesAllFilters,
} from "./eligibility";
import { autoFillSpacing, computeMaxRowsCols, fixed, type MaxRowsCols } from "./layout";
import { calcBlockReason, renderCtx, type RunEngineOptions } from "./render";
import type { CalcState } from "./state";
import type { CalculationInput } from "./types";

export const SOLUTIONS_MAX_SHOWN = 25; // maximum number of proposed solutions (user request)

export type SolutionRow = {
  family: string;
  diametro: number;
  lunghezza: number;
  sku: string;
  screwId: string;
  rows: number;
  cols: number;
  totale: number;
  offsetX: number; // centre offset (0 except CLT narrow element 2: grid shifted along the axis)
  offsetY: number;
  foro: "predrilled" | "nonPredrilled";
  rondella: string | null;
  etaTaglio: number;
  etaCombinata: number | null;
  etaAxSimple: number | null;
};

export type SolutionsProgress = { processed: number; total: number; found: number };

export type SolutionsResult =
  // fields to fill first (control ids of empty/invalid enabled visible inputs; "clt{n}UserLayers" = a layer row)
  | { kind: "missing"; missing: string[] }
  | {
      kind: "done";
      top: SolutionRow[]; // sorted and limited to SOLUTIONS_MAX_SHOWN
      totalCandidates: number;
      totalSolutions: number;
      // the current configuration is no longer valid and at least one solution exists: ask the user to pick one
      selectNewSolution: boolean;
    };

export type FindSolutionsOptions = RunEngineOptions & {
  solPreforo: boolean; // "Preforo": off = non-predrilled unless the ETA imposes predrilling, on = only predrilled
  solRondella: boolean; // "Rondella": off = no washer, on = every compatible washer
  signal?: AbortSignal;
  onProgress?: (p: SolutionsProgress) => void;
};

// Fields the search needs (number inputs and selects of the panels, enabled and visible), except the ones it fills
// itself (screw/length/grid/centre/washer, distances) and the project data.
export function collectMissingFields(ctx: EngineCtx): string[] {
  const v = ctx.v,
    { visible: vis, disabled: dis } = ctx.ui;
  const miss: string[] = [];
  const num = (id: keyof ControlValues, shown: boolean) => {
    if (shown && !Number.isFinite(v[id] as number)) miss.push(id);
  };
  const sel = (id: keyof ControlValues, shown: boolean) => {
    const val = v[id];
    if (shown && (val === "" || (typeof val === "number" && !Number.isFinite(val)))) miss.push(id);
  };
  num("thetaNum", true);
  for (const n of [1, 2] as const) {
    sel(`mat${n}Tipo` as const, true);
    const normal = vis[`mat${n}NormalFields` as const] && vis[`mat${n}SpecieClasseRow` as const];
    sel(`mat${n}Specie` as const, normal && (n === 2 || vis.mat1SpecieField));
    sel(`mat${n}Classe` as const, normal);
    const clt = vis[`mat${n}CltFields` as const];
    sel(`clt${n}Lato` as const, clt);
    sel(`clt${n}NumStrati` as const, clt);
    sel(`clt${n}Disposizione` as const, clt);
    sel(`clt${n}SpessoriMode` as const, clt && vis[`clt${n}SpessoriModeField` as const]);
    num(`clt${n}Spessore0` as const, clt && vis[`clt${n}Spessore0RowField` as const]);
    num(`clt${n}Spessore90` as const, clt && vis[`clt${n}Spessore0RowField` as const] && vis[`clt${n}Spessore90Field` as const]);
    sel(`clt${n}Classe` as const, clt && vis[`clt${n}ClasseField` as const]);
    if (clt && vis[`clt${n}UserLayersWrap` as const] && v[`clt${n}UserLayers` as const].some((l) => !Number.isFinite(l.spessore))) {
      miss.push(`clt${n}UserLayers` as const);
    }
    sel(`fi${n}` as const, vis[`fi${n}Field` as const]);
    num(`eps${n}` as const, vis[`eps${n}Field` as const] && !dis[`eps${n}` as const]);
  }
  num("b1", true);
  num("d1", vis.d1Field);
  num("spessore1", !dis.spessore1);
  num("rhok1", vis.rhok1Field && !dis.rhok1);
  num("rhomean1", vis.rhomean1Row);
  num("b2", !dis.b2);
  num("d2", vis.d2Field && !dis.d2);
  num("spessore2", !dis.spessore2);
  num("rhok2", !dis.rhok2);
  num("rhomean2", true);
  num("forceFn", true);
  num("forceFv", true);
  num("forceFa", true);
  sel("classeServizio", true);
  sel("durataCarico", true);
  sel("normativaGammaM", true);
  sel("gammaMColonna", vis.gammaMColonnaWrap);
  num("gammaM", !dis.gammaM);
  sel("etaForo", true);
  return miss;
}

// The current configuration is valid if a screw length is selected, all screws are in the envelope and all the
// active checks are ≤ 100%.
export function currentConfigurationValid(ctx: EngineCtx, s: CalcState | null): boolean {
  if (!ctx.v.etaFamiglia || !Number.isFinite(ctx.v.etaDiametro) || !Number.isFinite(ctx.v.etaLunghezza) || !s) return false;
  return (
    Number.isFinite(s.eta) &&
    s.eta <= 1 &&
    s.viteAmmissibili === s.viteTotali &&
    (!s.gridB || s.gridB.every((p) => p.ok)) &&
    (s.etaCombinata == null || s.etaCombinata <= 1) &&
    (s.etaAxSimple == null || s.etaAxSimple <= 1) &&
    (!s.three || s.etaSplitting == null || s.etaSplitting <= 1)
  );
}

const yieldToEventLoop = () => new Promise<void>((r) => setTimeout(r, 0));
const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new DOMException("Ricerca soluzioni annullata", "AbortError");
};

export async function findCompatibleSolutions(
  input: CalculationInput,
  catalog: EngineCatalog,
  opts: FindSolutionsOptions,
): Promise<SolutionsResult> {
  const { ctx: base, state: currentState } = renderCtx(input, catalog, opts);
  const mancanti = collectMissingFields(base);
  if (mancanti.length) return { kind: "missing", missing: mancanti };
  throwIfAborted(opts.signal);

  const ctx = cloneCtx(base);
  ctx.solutionsRunning = true;
  const v = ctx.v;
  const optPre = opts.solPreforo,
    optRond = opts.solRondella;
  v.rowsLinkedToMax = false;
  v.colsLinkedToMax = false;
  v.centroLinkedToMax = false;
  v.autoSpacing = true;
  v.centroOrizz = 0;
  v.centroVert = 0;

  const env = filterEnv(ctx); // module/service class/thicknesses/CLT before forcing the hole type
  v.etaForo = optPre ? "predrilled" : "nonPredrilled";
  v.useRondella = false;
  // hole type imposed by density/family (ETA-11/0030): the screws that require it are always evaluated predrilled,
  // whatever the search option
  const foroPerVite = (fam: string): SolutionRow["foro"] =>
    predrillRequirement(ctx, fam).required || optPre ? "predrilled" : "nonPredrilled";
  // set per screw here, not in screwPassesAllFilters: populateFamiglie shares it with one hole type for the whole menu
  const filtraCandidati = () =>
    catalog.SCREWS_DB.filter((s) => {
      v.etaForo = foroPerVite(s.family);
      return screwPassesAllFilters(ctx, s, env);
    });
  // smooth-shank prefilter A ≥ t1−20 (automatic search only), dropped when no screw is left with it
  ctx.partialAStrict = true;
  let candidati = filtraCandidati();
  if (candidati.length === 0) {
    ctx.partialAStrict = false;
    candidati = filtraCandidati();
  }
  v.etaForo = optPre ? "predrilled" : "nonPredrilled"; // the filter left it on the last screw evaluated

  const soluzioni: SolutionRow[] = [];
  let processed = 0;
  for (const cand of candidati) {
    v.etaFamiglia = cand.family;
    v.etaForo = foroPerVite(cand.family);
    v.etaDiametro = cand.geometria_mm.d1;
    v.etaLunghezza = cand.geometria_mm.L;
    autoFillSpacing(ctx);
    let bound: MaxRowsCols | null = null;
    try {
      bound = computeMaxRowsCols(ctx);
    } catch {
      bound = null;
    }
    // CLT narrow element 2: the row must slide along element 2's axis to respect the ETA end distances → the
    // grid offset of computeMaxRowsCols is evaluated (and then applied)
    const shiftCand = !!(env.clt2 && env.clt2.latoInserimento === "narrow" && bound);
    const offX = shiftCand ? bound!.offsetX : 0,
      offY = shiftCand ? bound!.offsetY : 0;
    v.centroOrizz = fixed(offX, 1);
    v.centroVert = fixed(offY, 1);
    const varianti: (string | null)[] = optRond ? rondelleCompatibiliList(cand) : [null];
    for (const codiceRondella of varianti) {
      if (codiceRondella) {
        v.useRondella = true;
        populateRondelle(ctx);
        v.rondellaCodice = codiceRondella;
      } else v.useRondella = false;
      if (bound && bound.rows > 0 && bound.cols > 0) {
        let found: { rows: number; cols: number; total: number; state: CalcState } | null = null;
        for (let rows = 1; rows <= bound.rows; rows++) {
          for (let cols = 1; cols <= bound.cols; cols++) {
            const total = rows * cols;
            if (found && total >= found.total) continue;
            v.rows = rows;
            v.cols = cols;
            const state = compute(ctx);
            if (calcBlockReason(ctx, state, true)) continue; // insufficient penetration: screw discarded
            const pienamenteAmmissibile = state.viteAmmissibili === total;
            const okTaglio = Number.isFinite(state.eta) && state.eta <= 1;
            const okComb = state.etaCombinata == null || state.etaCombinata <= 1;
            const okEstr = state.etaAxSimple == null || state.etaAxSimple <= 1;
            if (pienamenteAmmissibile && okTaglio && okComb && okEstr) found = { rows, cols, total, state };
          }
        }
        if (found) {
          soluzioni.push({
            family: cand.family,
            diametro: cand.geometria_mm.d1,
            lunghezza: cand.geometria_mm.L,
            sku: cand.sku,
            screwId: cand.id,
            rows: found.rows,
            cols: found.cols,
            totale: found.total,
            offsetX: offX,
            offsetY: offY,
            foro: foroPerVite(cand.family),
            rondella: codiceRondella,
            etaTaglio: found.state.eta,
            etaCombinata: found.state.etaCombinata,
            etaAxSimple: found.state.etaAxSimple,
          });
        }
      }
    }
    processed++;
    if (processed % 5 === 0 || processed === candidati.length) {
      opts.onProgress?.({ processed, total: candidati.length, found: soluzioni.length });
      await yieldToEventLoop();
      throwIfAborted(opts.signal);
    }
  }

  // Order: % shear descending (closest to 100% first), then % combined, then % withdrawal; checks not applicable
  // (Fa = 0 → null) count as -1.
  const keyOrNeg1 = (x: number | null) => (x == null ? -1 : x);
  soluzioni.sort((a, b) => {
    if (b.etaTaglio !== a.etaTaglio) return b.etaTaglio - a.etaTaglio;
    const cb = keyOrNeg1(b.etaCombinata),
      ca = keyOrNeg1(a.etaCombinata);
    if (cb !== ca) return cb - ca;
    return keyOrNeg1(b.etaAxSimple) - keyOrNeg1(a.etaAxSimple);
  });
  return {
    kind: "done",
    top: soluzioni.slice(0, SOLUTIONS_MAX_SHOWN),
    totalCandidates: candidati.length,
    totalSolutions: soluzioni.length,
    selectNewSolution: soluzioni.length >= 1 && !currentConfigurationValid(base, currentState),
  };
}

// "Usa questa": input patch of a solution. Rows/cols/offset are unlinked from the maximum; after applying it, run
// the engine with manualInputMode = false until the user edits a field.
export function applySolutionPatch(s: SolutionRow): Partial<CalculationInput> {
  return {
    preDrilled: s.foro === "predrilled",
    screwId: s.screwId,
    gridRowsLinkedToMax: false,
    gridColsLinkedToMax: false,
    gridOffsetLinkedToMax: false,
    gridRows: s.rows,
    gridCols: s.cols,
    gridOffsetX: fixed(s.offsetX || 0, 1),
    gridOffsetY: fixed(s.offsetY || 0, 1),
    washerCode: s.rondella || null,
  };
}
